import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, Github, Linkedin } from "lucide-react";
import { IDENTITY, SERVICES } from "@/data/content";
import { playDispatch } from "@/lib/sound";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
    "w-full bg-transparent border-b border-black/20 focus:border-vermilion outline-none px-0 py-2 text-sm text-[#1a1a1a] placeholder:text-neutral-400 transition-colors";

export default function ContactWindow() {
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const [services, setServices] = useState([]);
    const [sending, setSending] = useState(false);

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    const toggleService = (s) =>
        setServices((l) => (l.includes(s) ? l.filter((x) => x !== s) : [...l, s]));

    const onSubmit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            const message = services.length
                ? `[Services: ${services.join(", ")}]\n\n${form.message}`
                : form.message;
            await axios.post(`${API}/contact`, { ...form, message });
            playDispatch();
            toast.success("Mail sent — reply incoming.");
            setForm({ name: "", email: "", message: "" });
            setServices([]);
        } catch {
            toast.error("Send failed — the wire is down. Try again.");
        } finally {
            setSending(false);
        }
    };

    return (
        <div data-testid="contact-window" className="p-6 sm:p-8">
            <div className="flex items-center justify-between border-b border-black/10 pb-4 mb-6">
                <h2 className="text-xl font-bold tracking-tight">New Message — Geary</h2>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">draft autosaved</span>
            </div>
            <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-5">
                <div className="flex items-center gap-3 text-sm">
                    <span className="font-mono text-xs text-neutral-500 w-14">To:</span>
                    <span className="font-mono text-xs bg-black/[0.05] border border-black/10 rounded px-2 py-1">
                        {IDENTITY.email}
                    </span>
                </div>
                <div className="flex items-center gap-3">
                    <span className="font-mono text-xs text-neutral-500 w-14 shrink-0">From:</span>
                    <input
                        name="email"
                        type="email"
                        required
                        value={form.email}
                        onChange={onChange}
                        data-testid="contact-email-input"
                        placeholder="your@email.com"
                        className={inputCls}
                    />
                </div>
                <div className="flex items-center gap-3">
                    <span className="font-mono text-xs text-neutral-500 w-14 shrink-0">Name:</span>
                    <input
                        name="name"
                        required
                        value={form.name}
                        onChange={onChange}
                        data-testid="contact-name-input"
                        placeholder="Your name"
                        className={inputCls}
                    />
                </div>
                <div>
                    <span className="font-mono text-xs text-neutral-500">Tags:</span>
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid="contact-services">
                        {SERVICES.map((s) => (
                            <button
                                key={s}
                                type="button"
                                data-testid={`contact-service-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                onClick={() => toggleService(s)}
                                className={`font-mono text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border transition-colors ${
                                    services.includes(s)
                                        ? "bg-vermilion border-vermilion text-white"
                                        : "border-black/20 text-neutral-500 hover:border-black/50 hover:text-neutral-700"
                                }`}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                </div>
                <textarea
                    name="message"
                    required
                    rows={6}
                    value={form.message}
                    onChange={onChange}
                    data-testid="contact-message-input"
                    placeholder="Got a network on fire? A server with trust issues? Or just want to chat about homelab setups — write it here…"
                    className="w-full rounded-md border border-black/20 focus:border-vermilion outline-none p-3 text-sm leading-relaxed bg-black/[0.02] placeholder:text-neutral-400 resize-none transition-colors"
                />
                <div className="flex items-center justify-between pt-1">
                    <div className="flex gap-3">
                        <a href={IDENTITY.github} target="_blank" rel="noopener noreferrer" data-testid="contact-github-link" aria-label="GitHub" className="text-neutral-400 hover:text-vermilion transition-colors">
                            <Github size={17} />
                        </a>
                        <a href={IDENTITY.linkedin} target="_blank" rel="noopener noreferrer" data-testid="contact-linkedin-link" aria-label="LinkedIn" className="text-neutral-400 hover:text-vermilion transition-colors">
                            <Linkedin size={17} />
                        </a>
                    </div>
                    <button
                        type="submit"
                        disabled={sending}
                        data-testid="contact-submit-button"
                        className="inline-flex items-center gap-2 rounded-md bg-vermilion text-white px-6 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[#c93327] transition-colors disabled:opacity-50"
                    >
                        {sending ? (
                            <>
                                <Loader2 size={13} className="animate-spin" /> Sending…
                            </>
                        ) : (
                            <>
                                <Send size={13} /> Send
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
}
